import * as React from "react";
import { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Link, Route, Routes } from "react-router-dom";
import { RollingMagmaCalculator } from "./RollingMagmaCalculator";
import { RecombinationCalculator } from "./RecombinationCalculator";
import { ReplicaDustCalculator } from "./ReplicaDustCalculator";
import {
  resolveThemePreference,
  THEME_OPTIONS,
  type ResolvedTheme,
  type ThemePreference,
} from "../lib/theme";
import "./global.css";

// Base path of the site (injected by esbuild: "/" in dev, the GitHub Pages
// project-site path in production). See esbuild.mjs.
declare const APP_BASE: string;

const THEME_STORAGE_KEY = "poe-calculators-theme";

function getSavedThemePreference(): ThemePreference {
  try {
    const preference = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (THEME_OPTIONS.includes(preference as ThemePreference)) {
      return preference as ThemePreference;
    }
  } catch {
    // Storage can be unavailable in private/restricted browsing contexts.
  }
  return "system";
}

const ThemePicker: React.FC<{
  preference: ThemePreference;
  onChange: (preference: ThemePreference) => void;
}> = ({ preference, onChange }) => (
  <div className="theme-picker">
    <span id="theme-picker-label" className="theme-picker-label">
      Theme
    </span>
    <div
      className="theme-options"
      role="radiogroup"
      aria-labelledby="theme-picker-label"
    >
      {THEME_OPTIONS.map((option) => (
        <label
          className={`theme-option ${preference === option ? "selected" : ""}`}
          key={option}
        >
          <input
            type="radio"
            name="theme-preference"
            value={option}
            checked={preference === option}
            onChange={() => onChange(option)}
          />
          <span>{option[0].toUpperCase() + option.slice(1)}</span>
        </label>
      ))}
    </div>
    <span
      className="theme-tooltip-trigger"
      role="note"
      tabIndex={0}
      aria-describedby="theme-tooltip"
      aria-label="Theme tip"
    >
      <span aria-hidden="true">?</span>
      <span id="theme-tooltip" role="tooltip" className="theme-tooltip">
        you&apos;re welcome, sleepy
      </span>
    </span>
  </div>
);
ThemePicker.displayName = "ThemePicker";

const Home: React.FC = () => {
  return (
    <>
      <h1>
        <Link to="/">PoE Calculators</Link>
      </h1>
      <ul>
        <li>
          <Link to="/rolling-magma">Rolling Magma</Link>
        </li>
        <li>
          <Link to="/recombination">Recombination Odds</Link>
        </li>
        <li>
          <Link to="/replica-dust">Replica Unique Dust Values</Link>
        </li>
      </ul>
    </>
  );
};
Home.displayName = "Home";

const App: React.FC = () => {
  const [themePreference, setThemePreference] =
    useState<ThemePreference>(getSavedThemePreference);
  const [systemPrefersDark, setSystemPrefersDark] = useState(
    () => window.matchMedia("(prefers-color-scheme: dark)").matches
  );
  const resolvedTheme = resolveThemePreference(
    themePreference,
    systemPrefersDark
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const onSystemThemeChange = (event: MediaQueryListEvent) =>
      setSystemPrefersDark(event.matches);
    mediaQuery.addEventListener("change", onSystemThemeChange);
    return () => mediaQuery.removeEventListener("change", onSystemThemeChange);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = resolvedTheme;
  }, [resolvedTheme]);

  const changeThemePreference = (preference: ThemePreference) => {
    setThemePreference(preference);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, preference);
    } catch {
      // The current tab still changes themes if storage is unavailable.
    }
  };

  return (
    <div className="app-shell">
      <header className="app-toolbar">
        <ThemePicker
          preference={themePreference}
          onChange={changeThemePreference}
        />
      </header>
      {/* BrowserRouter gives clean URLs. The generated 404.html handles deep links on GitHub Pages. */}
      <BrowserRouter basename={APP_BASE}>
        <Routes>
          <Route index element={<Home />} />
          <Route path="/rolling-magma" element={<RollingMagmaCalculator />} />
          <Route path="/recombination" element={<RecombinationCalculator />} />
          <Route path="/replica-dust" element={<ReplicaDustCalculator />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
};
App.displayName = "App";

const root = document.getElementById("root");
ReactDOM.createRoot(root).render(<App />);
